import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import { openDB } from "idb";
import { v4 as uuidv4 } from "uuid";
import api from "../services/api";
import { telephoneNoMask } from "../functions/mask";
import { usePluginspace } from "../hooks/usePluginspace";
import { useAppConfig } from "../hooks/useAppConfig";

type FoodContextProviderProps = {
  children: ReactNode;
};

type OrderDataNFiscal = {
  orderType: "table" | "card" | "counter";
  orderId: string;
  number: number;
};

type FoodContextType = {
  isCard: boolean;
  setIsCard: (value: boolean) => void;
  isTable: boolean;
  setIsTable: (value: boolean) => void;
  isCounter: boolean;
  setIsCounter: Dispatch<SetStateAction<boolean>>;
  isInternalDelivery: boolean;
  setIsInternalDelivery: Dispatch<SetStateAction<boolean>>;
  isLandmark: boolean;
  setIsLandmark: Dispatch<SetStateAction<boolean>>;
  cardNumber: number;
  setCardNumber: Dispatch<SetStateAction<number>>;
  tableNumber: number;
  setTableNumber: Dispatch<SetStateAction<number>>;
  counterNumber: number;
  setCounterNumber: Dispatch<SetStateAction<number>>;
  landMarkNumber: number;
  setLandMarkNumber: Dispatch<SetStateAction<number>>;
  internalDeliveryNumber: number;
  setInternalDeliveryNumber: Dispatch<SetStateAction<number>>;
  tableName: string;
  setTableName: Dispatch<SetStateAction<string>>;
  cardName: string;
  setCardName: Dispatch<SetStateAction<string>>;
  internalDeliveryName: string;
  setInternalDeliveryName: Dispatch<SetStateAction<string>>;

  customerName: string;
  setCustomerName: Dispatch<SetStateAction<string>>;

  customerPhone: string;
  setCustomerPhone: Dispatch<SetStateAction<string>>;

  callWaiter: Date | undefined;
  setCallWaiter: Dispatch<SetStateAction<Date | undefined>>;

  setShouldMakeRequest: Dispatch<SetStateAction<boolean>>;
  shouldMakeRequest: boolean;

  //Variavel para controlar abertura de Modal para Realizar pedidos/Chamar garçon

  typeModal: "Chamar Garçom" | "Enviar pedido";
  setTypeModal: Dispatch<SetStateAction<"Chamar Garçom" | "Enviar pedido">>;

  openModal: boolean;
  setOpenModal: Dispatch<SetStateAction<boolean>>;

  vinculateDevice: (deviceId: string) => Promise<any>;

  verifyDevice: (deviceId: string) => Promise<any>;
  vinculateDeviceByWaiter: (password: string, deviceId: string) => Promise<any>;

  verifyToOrder: () => Promise<any>;
  loadUniqueDevice: () => Promise<string>;
  createUniqueDevice: () => Promise<string>;

  hasDeviceId: () => Promise<{
    deviceId: string;
    success: boolean;
  }>;

  calculateDistance: (v1: any, v2: any, v3: any, v4: any) => number;

  securityConfig: SecurityConfig | undefined;

  setOrderDataNFiscalL: Dispatch<SetStateAction<OrderDataNFiscal>>;
  getOrderDataNFiscal: () => OrderDataNFiscal | null;
};

type SecurityConfig = {
  id: number;
  pluginspaceId: number;
  locationRadius: number;
  accessMaximus: number;
  limitTime: number;
  password: string;
  created: string;
  updated: string;
  deleted: null;
};

export const FoodContext = createContext({} as FoodContextType);

export function FoodContextProvider(props: FoodContextProviderProps) {
  const { getToken } = usePluginspace();
  const { setTimeToValidate } = useAppConfig();

  const [isCard, setIsCard] = useState<boolean>(false);
  const [isTable, setIsTable] = useState<boolean>(false);
  const [isCounter, setIsCounter] = useState<boolean>(false);
  const [isInternalDelivery, setIsInternalDelivery] = useState<boolean>(false);
  const [isLandmark, setIsLandmark] = useState<boolean>(false);

  const [cardNumber, setCardNumber] = useState<number>(0);
  const [cardName, setCardName] = useState<string>("");

  const [tableNumber, setTableNumber] = useState<number>(0);
  const [tableName, setTableName] = useState<string>("");

  const [counterNumber, setCounterNumber] = useState<number>(0);
  const [landMarkNumber, setLandMarkNumber] = useState<number>(0);

  const [internalDeliveryNumber, setInternalDeliveryNumber] =
    useState<number>(0);
  const [internalDeliveryName, setInternalDeliveryName] = useState<string>("");

  const [customerName, setCustomerName] = useState<string>("");
  const [customerPhone, setCustomerPhone] = useState<string>("");

  const [callWaiter, setCallWaiter] = useState<Date | undefined>();

  //variavel para o modal
  const [typeModal, setTypeModal] = useState<"Chamar Garçom" | "Enviar pedido">(
    "Enviar pedido"
  );
  const [openModal, setOpenModal] = useState<boolean>(false);

  const [uniqueDevice, setUniqueDevice] = useState<string>("");

  const [shouldMakeRequest, setShouldMakeRequest] = useState<boolean>(false);

  const [securityConfig, setSecurityConfig] = useState<SecurityConfig>();

  const [orderDataNFiscal, setorderDataNFiscal] = useState<SecurityConfig>();

  function setOrderDataNFiscalL(data: any) {
    localStorage.setItem("orderDataNFiscal", JSON.stringify(data));
    setorderDataNFiscal(orderDataNFiscal);
  }
  function getOrderDataNFiscal() {
    if (orderDataNFiscal) {
      return orderDataNFiscal;
    }

    const orderData = localStorage.getItem("orderDataNFiscal");
    if (orderData) {
      return JSON.parse(orderData);
    }
    return null;
  }

  const initDB = async () => {
    const db: any = await openDB("DeviceDB", 1, {
      upgrade(db) {
        // Cria o objectStore chamado 'deviceStore' se não existir
        if (!db.objectStoreNames.contains("deviceStore")) {
          db.createObjectStore("deviceStore", { keyPath: "id" });
        }
      },
    });
    return db;
  };

  // Salva o GUID no IndexedDB
  const saveGuid = async (guid: string) => {
    const db = await initDB();
    await db.put("deviceStore", { id: "uniqueDeviceId", value: guid });
  };

  // Busca o GUID no IndexedDB
  const getGuid = async (): Promise<string | null> => {
    const db = await initDB();
    const result = await db.get("deviceStore", "uniqueDeviceId");
    return result ? result.value : null;
  };

  async function loadDataStorage() {
    try {
      const cardLandMarkLs = localStorage.getItem("landmark_synkar");
      const tableNumberLs = localStorage.getItem("table_number");
      const tableNameLs = localStorage.getItem("table_name");

      const cardNumberLs = localStorage.getItem("card_number");
      const cardNameLs = localStorage.getItem("card_name");

      const cardCounterLs = localStorage.getItem("counter_number");

      const cardInternalNumberLs = localStorage.getItem("internal_number");
      const cardInternalNameLs = localStorage.getItem("internal_name");

      const customerNameLs = localStorage.getItem("food_customer_name");
      const customerPhoneLs = localStorage.getItem("food_customer_phone");
      const callWaiterLs = localStorage.getItem("food_customer_call_waiter");

      if (cardLandMarkLs) {
        setIsLandmark(true);
        setLandMarkNumber(parseInt(window.atob(cardLandMarkLs)));
      }
      if (tableNumberLs && !cardNumberLs) {
        setIsTable(true);
        setTableNumber(parseInt(window.atob(tableNumberLs)));
      }
      if (tableNameLs) {
        setTableName(window.atob(tableNameLs));
      }
      if (cardNumberLs) {
        setIsCard(true);
        setCardNumber(parseInt(window.atob(cardNumberLs)));
      }
      if (cardNameLs) {
        setCardName(window.atob(cardNameLs));
      }

      if (cardCounterLs) {
        setIsCounter(true);
        setCounterNumber(parseInt(window.atob(cardCounterLs)));
      }
      if (cardInternalNumberLs) {
        setIsInternalDelivery(true);
        setInternalDeliveryNumber(parseInt(window.atob(cardInternalNumberLs)));
      }
      if (cardInternalNameLs) {
        setInternalDeliveryName(window.atob(cardInternalNameLs));
      }
      if (customerNameLs) {
        setCustomerName(customerNameLs);
      }
      if (customerPhoneLs) {
        setCustomerPhone(customerPhoneLs);
      }

      if (callWaiterLs) {
        setCallWaiter(new Date(JSON.parse(callWaiterLs)));
      }
      const devideOnline = await hasDeviceId();

      if (devideOnline && devideOnline.success) {
        const device = await verifyDevice(devideOnline.deviceId);
        if (device) {
          setCustomerName(device.data.customerName);
        }
      }
    } catch (error) {
      console.log(error);
    }
  }

  async function getGuidNinegrid() {
    try {
      setShouldMakeRequest(false);
      return api
        .post(
          "api/table-device/filter",
          { customerPhone: telephoneNoMask(customerPhone) },
          {
            headers: {
              Authorization: `Bearer ${await getToken()}`,
            },
          }
        )
        .then((res) => res.data)
        .catch((err) => err);
    } catch (error) {
      console.log(error);
    }
  }

  async function loadUniqueDevice() {
    try {
      const haveGuidNis = await getGuidNinegrid();
      const storedDeviceId = await getGuid();

      if (!haveGuidNis || !haveGuidNis.success) {
        if (storedDeviceId) {
          setUniqueDevice(storedDeviceId);

          return storedDeviceId;
        } else {
          const newDeviceId = uuidv4();
          await saveGuid(newDeviceId);
          setUniqueDevice(newDeviceId);

          return newDeviceId;
        }
      } else {
        const newDeviceId = haveGuidNis.data.deviceId;
        await saveGuid(newDeviceId);
        setUniqueDevice(newDeviceId);

        return newDeviceId;
      }
    } catch (error) {
      console.log(error);
      return "";
    }
  }

  async function hasDeviceId() {
    try {
      const storedDeviceId = await getGuid();

      if (storedDeviceId) {
        return {
          deviceId: storedDeviceId,
          success: true,
        };
      } else {
        return {
          deviceId: "",
          success: false,
        };
      }
    } catch (error) {
      console.log(error);
      return {
        deviceId: "",
        success: false,
      };
    }
  }

  async function createUniqueDevice() {
    try {
      const newDeviceId = uuidv4();
      await saveGuid(newDeviceId);
      setUniqueDevice(newDeviceId);

      return newDeviceId;
    } catch (error) {
      console.log(error);
      return "";
    }
  }

  useEffect(() => {
    loadDataStorage();
    getSecurityConfig();
  }, []);

  async function vinculateDevice(deviceId: string) {
    try {
      const table_CardNumber = isTable ? tableNumber : cardNumber;

      const vinculateDeviceDto = {
        tableNumber: table_CardNumber,
        deviceId: deviceId,
        customerName: customerName,
        typeDevice: isTable ? "table" : "card",
      };

      return await api
        .post("api/table-device", vinculateDeviceDto, {
          headers: {
            Authorization: `Bearer ${await getToken()}`,
          },
        })
        .then((res) => res.data)
        .catch((err) => err);
    } catch (error) {
      console.log(error);
    }
  }

  async function verifyDevice(deviceId: string) {
    try {
      return await api
        .get(`api/table-device/device/${deviceId}`, {
          headers: {
            Authorization: `Bearer ${await getToken()}`,
          },
        })
        .then((res) => res.data)
        .catch((err) => err);
    } catch (error) {
      console.log(error);
    }
  }

  async function vinculateDeviceByWaiter(password: string, deviceId: string) {
    try {
      const table_CardNumber = isTable ? tableNumber : cardNumber;
      const vinculateDeviceDto = {
        tableNumber: table_CardNumber,
        deviceId: deviceId,
        typeDevice: isTable ? "table" : "card",
        password: password,
      };

      return await api
        .post("api/table-device/vinculate-password", vinculateDeviceDto, {
          headers: {
            Authorization: `Bearer ${await getToken()}`,
          },
        })
        .then((res) => res.data)
        .catch((err) => err);
    } catch (error) {
      console.log(error);
    }
  }

  async function getSecurityConfig() {
    try {
      await api
        .get("api/security-config", {
          headers: {
            Authorization: `Bearer ${await getToken()}`,
          },
        })
        .then((res) => {
          const data = res.data.data;
          if (data) {
            setSecurityConfig(data);

            setTimeToValidate(data.limitTime);
          }
        })
        .catch((err) => console.log(err));
    } catch (error) { }
  }

  async function verifyToOrder() {
    try {
      const tableNumberDevice = isCard ? cardNumber : tableNumber;
      return api
        .post(
          "api/table-device/filter",
          {
            deviceId: uniqueDevice,
            tableNumber: tableNumberDevice,
          },
          {
            headers: {
              Authorization: `Bearer ${await getToken()}`,
            },
          }
        )
        .then((res) => res.data)
        .catch((err) => err);
    } catch (error) {
      console.log(error);
    }
  }

  function calculateDistance(lat1: any, lon1: any, lat2: any, lon2: any) {
    const r = 6371; // km
    const p = Math.PI / 180;

    const a =
      0.5 -
      Math.cos((lat2 - lat1) * p) / 2 +
      (Math.cos(lat1 * p) *
        Math.cos(lat2 * p) *
        (1 - Math.cos((lon2 - lon1) * p))) /
      2;

    return 2 * r * Math.asin(Math.sqrt(a));
  }

  const setOnlyCard = (value: boolean) => {
    if (value) {
      setIsTable(false);
      setIsCounter(false);
      setIsInternalDelivery(false);
      setIsLandmark(false);
    }
    setIsCard(value);
  };

  const setOnlyTable = (value: boolean) => {
    if (value) {
      setIsCard(false);
      setIsCounter(false);
      setIsInternalDelivery(false);
      setIsLandmark(false);
    }
    setIsTable(value);
  };

  return (
    <FoodContext.Provider
      value={{
        isCard,
        setIsCard: setOnlyCard, // ✅ Usar função que garante exclusividade
        isTable,
        setIsTable: setOnlyTable,
        isCounter,
        setIsCounter,
        isInternalDelivery,
        setIsInternalDelivery,
        isLandmark,
        setIsLandmark,
        cardNumber,
        setCardNumber,
        tableNumber,
        setTableNumber,
        counterNumber,
        setCounterNumber,
        landMarkNumber,
        setLandMarkNumber,
        internalDeliveryNumber,
        setInternalDeliveryNumber,
        tableName,
        setTableName,
        cardName,
        setCardName,
        internalDeliveryName,
        setInternalDeliveryName,
        customerName,
        setCustomerName,
        typeModal,
        setTypeModal,
        openModal,
        setOpenModal,
        customerPhone,
        setCustomerPhone,
        callWaiter,
        setCallWaiter,

        vinculateDevice,
        verifyDevice,
        setShouldMakeRequest,
        shouldMakeRequest,
        verifyToOrder,
        vinculateDeviceByWaiter,
        securityConfig,
        calculateDistance,
        loadUniqueDevice,
        setOrderDataNFiscalL,
        getOrderDataNFiscal,
        createUniqueDevice,
        hasDeviceId,
      }}
    >
      {props.children}
    </FoodContext.Provider>
  );
}
